import { type File, useStores } from '@littlebird/sdk';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { logError } from '@/lib/errorLogging';
import { handleCopyText } from '@/utils/platform';

const COPY_FAILED_MESSAGE =
  'Could not copy. Please try again or download the file.';

export function useCopyFileText(context: string) {
  const { files: filesStore } = useStores();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 500);
    return () => clearTimeout(timer);
  }, [copied]);

  const copyFileText = async (file: File) => {
    try {
      const url = await filesStore.getFileUrl(file);
      if (!url) {
        toast.error(COPY_FAILED_MESSAGE);
        return;
      }
      const response = await fetch(url);
      if (!response.ok) {
        toast.error(COPY_FAILED_MESSAGE);
        return;
      }
      await handleCopyText(await response.text());
      setCopied(true);
    } catch (error) {
      logError(error, 'Failed to copy file', {
        context,
        metadata: { fileId: file.id },
      });
      toast.error(COPY_FAILED_MESSAGE);
    }
  };

  return { copied, copyFileText };
}
